import { Button, Input, Separator } from '@design-systems/canvas-ui'
import { useLocation, useNavigate } from 'react-router-dom'
import Icon, { CloseIcon } from '@design-systems/react-icons'
import { useState } from 'react'
import { useAuth } from '@praxis/component-auth'

import { RawLocation, UserDetailsData } from '../../queries/getUserDetailsQuery'
import { useMPMEnv } from '../../utils/env'

import './location-search.css'
import { useUser } from '../../components/user-context'
import { useNearbyLocations } from 'components/nearby-locations'
import { useUserDetails } from 'hooks/userDetails/useUserDetails'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { NearbyLocation } from 'queries/getNearbyLocationsQuery/getNearbyLocationsQuery'
import { ErrorFallback } from 'components/error-fallback'
import { ErrorBoundary } from 'react-error-boundary'
import { t } from 'i18next'
import { BackButton } from 'components/button/back-button'
import { MPMLayout } from 'components/layout'

export const LocationSearchPage = () => {
    const { locationSearch } = TRANSLATION_KEYS

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(locationSearch.header)}
                />
            )}
        >
            <LocationSearch />
        </ErrorBoundary>
    )
}

interface Location {
    id: number | string
    name: string
    shortName: string
}

const getLocationsMatchingSearchTerm = (
    rawLocationData: UserDetailsData['locations'] | undefined,
    searchQuery: string,
): Location[] => {
    if (searchQuery.trim().length === 0 || rawLocationData === undefined) {
        return []
    }

    const locationsMatchingSearchTerm = Object.values(rawLocationData)
        .flat()
        .filter(
            (rawLocation) =>
                rawLocation?.id && rawLocation?.name && rawLocation?.short_name,
        )
        .map((rawLocation) => ({
            id: rawLocation.id,
            name: rawLocation.name,
            shortName: rawLocation.short_name,
        }))
        .filter(
            (location) =>
                `${location.id}`
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                location.shortName
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                location.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )

    return locationsMatchingSearchTerm
}

interface LocationSearchResultProps {
    locationId: string | number
    locationShortName: string
    locationName: string
    distanceAway?: number
}

export const LocationSearchResult = ({
    locationId,
    locationShortName,
    locationName,
    distanceAway,
}: LocationSearchResultProps) => {
    const { setLocation, location } = useUser()
    const navigate = useNavigate()
    const {
        state: { previousLocationPathname },
    } = useLocation()
    const locationSearchResultOnClickHandler = () => {
        setLocation({
            ...location,
            locationId: locationId.toString(),
            shortName: locationShortName,
            name: locationName,
        })

        const isBrandDetailsRoute =
            previousLocationPathname.startsWith('/brands/')
        let newPath: string
        if (isBrandDetailsRoute) {
            newPath = `/brands?locationId=${locationShortName}`
        } else {
            const prevUrl = new URL(
                previousLocationPathname,
                window.location.origin,
            )
            prevUrl.searchParams.delete('locationId')
            prevUrl.searchParams.set('locationId', locationShortName)
            newPath = prevUrl.pathname + prevUrl.search
        }

        void navigate(newPath)
    }

    return (
        <div
            className="location-search-result"
            role="button"
            onClick={locationSearchResultOnClickHandler}
        >
            <div>
                <div className="location-search-result-short-name">
                    {locationShortName}
                </div>
                <div>{locationName}</div>
            </div>
            {distanceAway && (
                <div className="location-search-result-distance-away">{`${distanceAway.toFixed(
                    2,
                )} mi`}</div>
            )}
        </div>
    )
}

interface LocationMatchNotFoundProps {
    isLoading?: boolean
    locationSearchResults?: Location[]
    searchTerm: string
}

export const LocationMatchNotFound = ({
    isLoading,
    locationSearchResults,
    searchTerm,
}: LocationMatchNotFoundProps) => {
    const { locationSearch } = TRANSLATION_KEYS
    return (
        <span>
            {!isLoading &&
                searchTerm &&
                locationSearchResults?.length === 0 && (
                    <span className="hc8-clr-alert" aria-live="polite">
                        {t(locationSearch.matchNotFound)}
                    </span>
                )}
        </span>
    )
}

export function filterLocationsById(
    nearbyLocations: NearbyLocation[],
    userDetailLocations: RawLocation[],
): NearbyLocation[] {
    const validIds = new Set(
        userDetailLocations
            .filter((location) => location?.id)
            .map((location) => location.id),
    )
    return nearbyLocations.filter(
        (location) =>
            location?.location_id && validIds.has(location.location_id),
    )
}

export const LocationSearch = () => {
    const { locationSearch } = TRANSLATION_KEYS
    const [searchQuery, setSearchQuery] = useState('')
    const { session } = useAuth()
    const lanId = session?.userInfo?.lanId ?? ''
    const env = useMPMEnv()
    const { nearbyLocations } = useNearbyLocations()
    const { data, isLoading } = useUserDetails({
        env,
        accessToken: session?.accessToken ?? '',
        lanId,
    })
    const locationSearchResults = getLocationsMatchingSearchTerm(
        data?.locations,
        searchQuery,
    )

    const userDetailsLocations = data?.locations?.store_location_details ?? []

    const filteredNearbyLocations = filterLocationsById(
        nearbyLocations ?? [],
        userDetailsLocations,
    )

    return (
        <MPMLayout
            heading={t(locationSearch.header)}
            breadcrumbsEnabled={false}
            isMenuOverride
            menuOverride={<BackButton />}
            topNavRightButton={<></>}
            hideFeedback
        >
            <div className="location-search-sticky-input">
                <Input.Label htmlFor="location-search-input">
                    {t(locationSearch.searchLabel)}
                </Input.Label>
                <div className="location-search-input-container">
                    <Input.Text
                        id="location-search-input"
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            setSearchQuery(event.target.value)
                        }}
                        className="location-search-input"
                        value={searchQuery}
                    />
                    <Button
                        isIconOnly
                        id="location-search-input-clear-button"
                        aria-label={t(locationSearch.clearSearchAriaLabel)}
                        onClick={() => setSearchQuery('')}
                        variant="ghost"
                    >
                        <Icon svg={CloseIcon} aria-hidden />
                    </Button>
                </div>
            </div>
            <div className="location-search-content-container">
                <LocationMatchNotFound
                    isLoading={isLoading}
                    locationSearchResults={locationSearchResults}
                    searchTerm={searchQuery}
                />
                {(!searchQuery || locationSearchResults.length === 0) && (
                    <>
                        {!searchQuery && (
                            <h2 className="nearby-locations-heading">
                                {t(locationSearch.nearbyLocationsHeader)}
                            </h2>
                        )}
                        {filteredNearbyLocations.map(
                            (nearbyLocation, nearbyLocationIndex) => {
                                const locationName =
                                    nearbyLocation.location_names?.[0]?.name ||
                                    nearbyLocation.address.address_line1
                                return (
                                    <div
                                        key={`nearby-location-${nearbyLocation.location_id}-${locationName}`}
                                    >
                                        <LocationSearchResult
                                            locationId={
                                                nearbyLocation.location_id
                                            }
                                            locationShortName={`T${nearbyLocation.location_id
                                                .toString()
                                                .padStart(4, '0')}`}
                                            locationName={locationName}
                                            distanceAway={
                                                nearbyLocation.distance
                                            }
                                        />
                                        {nearbyLocationIndex !==
                                            nearbyLocations.length - 1 && (
                                            <Separator />
                                        )}
                                    </div>
                                )
                            },
                        )}
                    </>
                )}
                {!isLoading &&
                    locationSearchResults.map((location, locationIndex) => (
                        <div key={`${location.name}-${location.shortName}`}>
                            <LocationSearchResult
                                locationId={location.id}
                                locationShortName={location.shortName}
                                locationName={location.name}
                            />
                            {locationIndex !==
                                locationSearchResults.length - 1 && (
                                <Separator />
                            )}
                        </div>
                    ))}
            </div>
        </MPMLayout>
    )
}
